/**
 * DaVinci Payment Schedule Extractor
 *
 * Extracts the down-payment / balance split from a DaVinci BA (Booking Action)
 * response, so the payment step can tell the customer what is due when.
 *
 * Model (see `claude/specs/PaymentSplit/FINDINGS.md`):
 * - `Booking.OpenItems` is present in EVERY response — its presence says nothing.
 *   The split is signalled solely by a numeric `OpenItems.Deposit.Amount.Foreign`.
 * - With no split, `Deposit.Amount` is an empty object `{}` and the whole sum
 *   sits in `Balance` (whose amount then equals `TotalSalesPrice`). That empty
 *   object must never be coerced to `0` — it means "no deposit", not "0 €".
 * - `TermOfPayment` is `1` either way, so it cannot be used to discriminate.
 * - DaVinci only splits when departure is far enough out (~44+ days); short-term
 *   bookings are payable in one amount.
 * - Due dates arrive as local midnight with a UTC offset attached
 *   (`/Date(1790287200000+0200)/`). The offset is carried through to the view so
 *   the calendar day renders correctly — see {@link PaymentInstallment.dueDateOffsetMinutes}.
 *
 * Note: the test gateway never returns a deposit; only production does.
 */

import { tryParseDaVinciDate } from '@/services/api/davinci/helpers';

export interface PaymentInstallment {
  amount: number;
  dueDate: Date | null;
  dueDateOffsetMinutes: number;
}

export interface PaymentSchedule {
  deposit: PaymentInstallment;
  balance: PaymentInstallment;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function readInstallment(item: unknown): PaymentInstallment | null {
  const record = asRecord(item);
  if (!record) return null;

  const amount = asRecord(record.Amount)?.Foreign;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return null;

  const parsed = tryParseDaVinciDate(asRecord(record.Date)?.Value);

  return {
    amount,
    dueDate: parsed?.date ?? null,
    dueDateOffsetMinutes: parsed?.offsetMinutes ?? 0,
  };
}

export function extractPaymentScheduleFromBookingResponse(
  bookingResponse: Record<string, unknown>
): PaymentSchedule | null {
  const openItems = asRecord(bookingResponse.OpenItems);
  if (!openItems) return null;

  const deposit = readInstallment(openItems.Deposit);
  if (!deposit) return null;

  // A deposit without a balance is not a renderable breakdown.
  const balance = readInstallment(openItems.Balance);
  if (!balance) return null;

  return { deposit, balance };
}

/**
 * The displayed rows must add up for the customer.
 *
 * DaVinci splits the accommodation total only; the checkout's `totalPrice`
 * additionally carries insurance and extras. Those are always 0 today
 * (`HIDE_AUXILIARIES_STEP`), but if that flag is ever flipped the rows would
 * stop reconciling — hide the breakdown rather than show arithmetic that does
 * not work out.
 */
export function isPaymentScheduleReconciled(
  schedule: PaymentSchedule | null,
  totalPrice: number
): boolean {
  if (!schedule) return false;
  return Math.abs(schedule.deposit.amount + schedule.balance.amount - totalPrice) < 0.01;
}
